import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Globe, FileText, Briefcase } from 'lucide-react';
import { useSiteContent } from '@/context/SiteContentContext';
import { PageHero } from '@/components/common/PageHero';

interface SitemapData {
  baseUrl: string;
  companyName: string;
  staticPages: Array<{ path: string; title: string }>;
  pages: Array<{ slug: string; title: string }>;
  blogs: Array<{ slug: string; title: string; category: string | null }>;
}

export const SitemapPage: React.FC = () => {
  const { template } = useSiteContent();

  const { data, isLoading } = useQuery({
    queryKey: ['html-sitemap'],
    queryFn: async () => {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-sitemap?format=html-data`;
      const res = await fetch(url, {
        headers: { 
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
      });
      if (!res.ok) throw new Error('Failed to load sitemap data');
      return await res.json() as SitemapData;
    },
  });

  return (
    <>
      <Helmet>
        <title>Sitemap | {template.brand?.name || 'Services'}</title>
        <meta name="description" content={`Complete sitemap for ${template.brand?.name || 'our website'}. Browse all pages, services, and blog posts.`} />
      </Helmet>

      <PageHero title="Sitemap" subtitle="Browse all pages on our website" size="sm" />

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : data ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {/* Main Pages */}
            <div>
              <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Main Pages
              </h2>
              <ul className="space-y-2">
                {data.staticPages.map((page) => (
                  <li key={page.path}>
                    <Link
                      to={page.path}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {page.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Dynamic Pages */}
            {data.pages.length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Pages
                </h2>
                <ul className="space-y-2">
                  {data.pages.map((page) => (
                    <li key={page.slug}>
                      <Link
                        to={`/${page.slug}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {page.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Blog Posts */}
            {data.blogs.length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-primary" />
                  Blog Posts
                </h2>
                <ul className="space-y-2">
                  {data.blogs.map((blog) => (
                    <li key={blog.slug}>
                      <Link
                        to={`/blog/${blog.slug}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {blog.title}
                        {blog.category && (
                          <span className="ml-2 text-xs text-muted-foreground/60">({blog.category})</span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-center">Unable to load sitemap data.</p>
        )}
      </div>
    </>
  );
};

export default SitemapPage;